import $ = require('jquery');
import l10n from '../util/l10n';

import Iie from '../image-inline-editor/index';
import IieDefaultPayload from '../image-inline-editor/core/DefaultPayload';
import ModulWithRedux from './ModulWithRedux';
import ModulMatrix from './ModulMatrix';
import WebView from './image/WebView';
import EditView from './image/EditView';
import EditViewCrop from './image/EditViewCrop';
import EditViewCaptions from './image/EditViewCaptions';
import InPlaceEditView from './image/InPlaceEditView';
import {
    ImageModuleBaseData,
    ImageModuleData,
    ImageRegenerationData
} from './image/interfaces';
import {loadImage} from '../util/image/load';
import {getCachedImage, clearCachedImage} from '../util/image/cache';
import {
    getIieSourceImageUrl, getIieSourceImageKey, getIieSourceImageWidth, getIieSourceImageHeight
} from './image/util';
import {OpenConfig} from './Modul';
import {update} from './index';
import AppRegistry from '../AppRegistry';
import TabOptions from '../actionBar/interfaces/TabOptions';
import Model from '../image-inline-editor/core/Model';

function getStore() {
    return AppRegistry.getStoreProvider().getStore();
}

/**
 * Base class for modules with images
 */
export default class ModulWithImage extends ModulWithRedux<ImageModuleBaseData> {
    static MAX_IMAGE_WIDTH = 1024;
    static MAX_IMAGE_WIDTH_IN_MATRIX = 512;
    static MAX_IMAGE_FULL_WIDTH = 1920;

    CONFIG_MIN_WIDTH = 900;
    hasText = false;
    hasSpotlightBorder = false;
    autoOpenImageSelector = true;
    saveTimeout = 5 * 60000;

    protected data: ImageModuleBaseData;
    private initialDataString: string;
    private initialRegenerationData: ImageRegenerationData;

    getActionBarTabs(): TabOptions[] {
        return [
            {label: l10n('module.image.tabs.basic'), content: this.getConnectedComponent(EditView)},
            {label: l10n('module.image.tabs.crop'), content: this.getConnectedComponent(EditViewCrop)},
            {label: l10n('module.image.tabs.caption'), content: this.getConnectedComponent(EditViewCaptions)}
        ];
    }

    getInPlaceEditViewClass() {
        return InPlaceEditView;
    }

    getWebViewClass() {
        return WebView;
    }

    constructor(moduleId: number) {
        super(moduleId);
        this.initialDataString = JSON.stringify(this.data);
    }

    /**
     * @override
     * purely for typing purposes
     */
    getData() {
        return this.data;
    }

    // TODO: move to modul.js
    inSidebar() {
        return this.modul.closest(".diywebSecondary").length > 0;
    }

    open(openConfig?: OpenConfig) {
        if (!this.isNew) {
            $(document.body).trigger('ContentModuleOpened.BSA');
        }
        loadImage(this.getImageElement()).then(image => {
            // initialize visual image size
            this.data.visualImageHeight = image.offsetHeight;
            this.data.visualImageWidth = image.offsetWidth;
            // some predefined modules (like ones in matrix module)
            // have images fit by size but imageFit flag is wrongly
            // set to false, so we correct it here
            if (
                !(this.data as ImageModuleData).isFullwidth
                && Math.round(this.getMaxWidth()) === image.offsetWidth
            ) {
                this.data.imageFit = true;
            }
        }
        ).then(() => {
            super.open(openConfig);
            window.addEventListener('resize', this.onWindowResize);
        });

        this.initialRegenerationData = {
            width: this.data.imageWidth,
            height: this.data.imageHeight,
            url: getIieSourceImageUrl(this.data),
            payload: Object.assign({}, IieDefaultPayload, this.data.iiePayload)
        };
    }

    close(userClose?: boolean, cancelEdit?: boolean, isSaving?: boolean, isUserAction?: boolean) {
        super.close(userClose, cancelEdit, isSaving, isUserAction);
        clearCachedImage('transformedImageUrl' + this.moduleId);
        clearCachedImage('origImageUrl' + this.moduleId);
        window.removeEventListener('resize', this.onWindowResize);
    }

    isCropped() {
        const data = this.data;
        const sourceAspect = getIieSourceImageWidth(data) / getIieSourceImageHeight(data);
        const cropAspect = data.imageWidth / data.imageHeight;
        if (data.imageAnimated || !data.iiePayload) {
            return false;
        }
        return Math.abs(sourceAspect - cropAspect) > 0.03 || data.iiePayload.zoom !== 1;
    }

    needsRegeneration(data: ImageModuleBaseData) {
        const initialData = this.initialRegenerationData;
        const finalData: ImageRegenerationData = {
            width: data.imageWidth,
            height: data.imageHeight,
            url: getIieSourceImageUrl(data),
            payload: data.iiePayload
        };
        if (!initialData.payload || !finalData.payload) {
            return true;
        }
        return initialData.width !== finalData.width
            || initialData.height !== finalData.height
            || initialData.url !== finalData.url
            || initialData.payload.zoom !== finalData.payload.zoom
            || initialData.payload.cropXRatio !== finalData.payload.cropXRatio
            || initialData.payload.cropYRatio !== finalData.payload.cropYRatio
        ;
    }

    getSaveData() {
        const data = super.getSaveData();
        const cropped = this.isCropped();
        const payload = data.iiePayload || IieDefaultPayload;

        const sourceWidth = getIieSourceImageWidth(data);
        const sourceHeight = getIieSourceImageHeight(data);
        const width = Math.round(data.imageWidth);
        const height = Math.round(data.imageHeight);

        const model = new Model(
            payload,
            sourceWidth,
            sourceHeight,
            data.visualImageWidth,
            data.visualImageHeight,
            this.getMaxWidth()
        );
        const absZoom = model.getAbsoluteZoom();

        return Object.assign(data, {
            iiePayload: JSON.stringify(payload),
            imageFit: data.imageFit ? 1 : 0,
            imageWidth: width,
            imageHeight: height,
            imageCrop: cropped ? {
                left: Math.round(model.getCropLeft() / absZoom),
                top: Math.round(model.getCropTop() / absZoom),
                width: Math.round(model.getCropWidth() / absZoom),
                height: Math.round(model.getCropHeight() / absZoom)
            } : '',
            needsRegeneration: this.needsRegeneration(data) ? 1 : 0
        });
    }

    save() {
        if (this.getIsNew() !== true && (this.initialDataString !== JSON.stringify(this.data))) {
            $(document.body).trigger('ContentModuleSaved.BSA');
        }
        super.save();
    }

    /* @override */
    updateModulContent(html: string) {
        // We don't want html from server to update our webview
        // we render it ourselves
        // NO this._super(html);
    }

    getImageElement(imageId?: number): HTMLImageElement {
        if (imageId) {
            return document.getElementById("image_" + imageId) as HTMLImageElement;
        } else {
            return this.getContent().querySelector("img") as HTMLImageElement;
        }
    }

    getContent(): HTMLElement {
        return document.querySelector("#modul_" + this.moduleId + " .align-container") as HTMLElement;
    }

    getMaxWidth(): number {
        return parseFloat(getComputedStyle(this.getContentElement()).width);
    }

    getIieSourceImage = () => {
        return  getCachedImage(
            getIieSourceImageUrl(this.data),
            getIieSourceImageKey(this.data) + this.moduleId
        );
    };

    private generateImage() {
        return this.getIieSourceImage().then((image: HTMLImageElement) => {
            const data = this.data;
            let fullResolution = false;
            let maxWidth: number = null;
            if ((data as ImageModuleData).isFullwidth) {
                fullResolution = true;
                maxWidth = ModulWithImage.MAX_IMAGE_FULL_WIDTH;
            } else if (data.imageFit) {
                fullResolution = true;
                if (this.getMatrixModuleParent() instanceof ModulMatrix) {
                    maxWidth = ModulWithImage.MAX_IMAGE_WIDTH_IN_MATRIX;
                } else {
                    maxWidth = ModulWithImage.MAX_IMAGE_WIDTH;
                }
            }

            const options = {
                sourceImage: image,
                fullResolution: fullResolution,
                cropWidth: data.visualImageWidth,
                cropHeight: data.visualImageHeight,
                payload: data.iiePayload,
                mediaType: data.imageMediaType,
                maxWidth: maxWidth
            };
            return Iie.generate(options);
        });
    }

    private onWindowResize = () => {
        // on window resize visual size of the image may change because of the
        // flexible layout, these will be recalculated after a delay
        if (!this.data.imageAnimated) {
            this.generateImage()
            .then((result) => {
                const partialPayload = {
                    imageUrl: result.imageDataURL,
                    visualImageHeight: null as number,
                    visualImageWidth: null as number
                };
                getStore().dispatch(update({
                    moduleId: this.moduleId,
                    partialPayload
                }));

                setTimeout(this.updateVisualImageSize, 100);
            });
        }
    };

    private updateVisualImageSize = () => {
        const imageEl = this.getImageElement();
        getStore().dispatch(update({
            moduleId: this.moduleId,
            partialPayload: {
                visualImageHeight: imageEl.offsetHeight,
                visualImageWidth: imageEl.offsetWidth
            }
        }));
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/ModulWithImage.ts
 **/